import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ColorScheme, ThemeMode } from "../theme/index";

type WebThemeContextValue = {
	mode: ThemeMode;
	scheme: ColorScheme;
	setMode: (mode: ThemeMode) => void;
};

const WebThemeContext = createContext<WebThemeContextValue | null>(null);

const resolveScheme = (mode: ThemeMode): ColorScheme => {
	if (mode === ThemeMode.SYSTEM) {
		const prefersDark = typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
		return prefersDark ? ColorScheme.DARK : ColorScheme.LIGHT;
	}
	return mode === ThemeMode.DARK ? ColorScheme.DARK : ColorScheme.LIGHT;
};

const applyScheme = (scheme: ColorScheme): void => {
	if (typeof document === "undefined") return;
	const root = document.documentElement;
	root.dataset.theme = scheme;
	root.classList.toggle("dark", scheme === ColorScheme.DARK);
	root.classList.toggle("light", scheme === ColorScheme.LIGHT);
};

type WebThemeProviderProps = {
	defaultMode?: ThemeMode;
	children: React.ReactNode;
};

export const WebThemeProvider: React.FC<WebThemeProviderProps> = ({ defaultMode = ThemeMode.SYSTEM, children }) => {
	const [mode, setMode] = useState<ThemeMode>(defaultMode);
	const scheme = useMemo(() => resolveScheme(mode), [mode]);

	useEffect(() => {
		applyScheme(scheme);
	}, [scheme]);

	useEffect(() => {
		if (mode !== ThemeMode.SYSTEM || typeof window === "undefined") return;
		const query = window.matchMedia("(prefers-color-scheme: dark)");
		const listener = (): void => applyScheme(resolveScheme(ThemeMode.SYSTEM));
		query.addEventListener("change", listener);
		return () => query.removeEventListener("change", listener);
	}, [mode]);

	const value = useMemo<WebThemeContextValue>(() => ({ mode, scheme, setMode }), [mode, scheme]);

	return <WebThemeContext.Provider value={value}>{children}</WebThemeContext.Provider>;
};

export const useWebTheme = (): WebThemeContextValue => {
	const context = useContext(WebThemeContext);
	if (!context) {
		throw new Error("useWebTheme must be used within a WebThemeProvider");
	}
	return context;
};
